import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PasswordInput = ({ id, label, value, onChange, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="overline overline-muted block mb-2">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          data-testid={testId}
          className="w-full pl-4 pr-11 py-3 bg-paper border border-ink-line rounded text-ink text-sm outline-none focus:border-ink"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide" : "Show"}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-ink-soft hover:text-ink"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
};

const AdminChangePasswordPage = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("New password and confirmation don&apos;t match.");
    if (next === current) return setError("New password must differ from the current one.");
    setSaving(true);
    try {
      await api.changePassword(current, next);
      toast.success("Password updated.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl" data-testid="admin-change-password-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="admin-change-password-back"
        className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-[11px] font-mono-editorial uppercase tracking-[0.22em]"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mt-6 flex items-center gap-3">
        <KeyRound size={22} strokeWidth={1.4} className="text-ink" />
        <h1 className="font-display text-ink text-3xl md:text-4xl leading-[1.06]">Change password</h1>
      </div>
      <p className="mt-2 text-ink-soft text-sm max-w-md">
        Choose a strong password of at least 8 characters. You'll stay signed in on this device.
      </p>

      <form onSubmit={submit} className="mt-8 editorial-card p-6 md:p-8 space-y-5" data-testid="admin-change-password-form">
        <PasswordInput id="cp-current" label="Current password" value={current} onChange={setCurrent} testId="cp-current-input" />
        <PasswordInput id="cp-new" label="New password" value={next} onChange={setNext} testId="cp-new-input" />
        <PasswordInput id="cp-confirm" label="Confirm new password" value={confirm} onChange={setConfirm} testId="cp-confirm-input" />

        {error && (
          <div className="text-[13px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert" data-testid="cp-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/admin")}
            data-testid="cp-cancel"
            className="px-5 py-2.5 text-xs text-ink-soft hover:text-ink border border-ink-line rounded-full"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="cp-submit"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-ink text-paper text-xs font-medium rounded-full hover:bg-gold-dark transition-colors disabled:opacity-40"
          >
            {saving ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminChangePasswordPage;
